import { Component, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    // Only clear non-auth keys that may hold corrupted data.
    // Preserving accessToken keeps the user logged in after reload.
    const preserveKeys = ["accessToken"];
    try {
      const preserved: Record<string, string> = {};
      for (const key of preserveKeys) {
        const val = localStorage.getItem(key);
        if (val !== null) preserved[key] = val;
      }
      localStorage.clear();
      for (const [key, val] of Object.entries(preserved)) {
        localStorage.setItem(key, val);
      }
    } catch {
      // ignore
    }
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h5">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            An unexpected error occurred. Clearing local data and reloading may
            fix the issue.
          </Typography>
          <Button variant="contained" onClick={this.handleReset}>
            Clear & Reload
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
